'use strict';

const {
    ASSET_ID_DESCRIPTION,
    ASSET_ID_DESKTOP_IMG,
    ASSET_ID_MOBILE_IMG,
    ADFORM_ASSETS_DOMAIN_REPLACEMENT,
    getAdformAssetsDomainRegex
} = require('../adformConstants');

const { getAdformConfig } = require('../common/jsConfig');
const adformTrackReplaceURL = getAdformConfig('adform_track_replace_URL');

/**
 * Applies necessary logic on adform assets url
 * (e.g. replace image url domain with our to reduce risk of detection by adblockers)
 * @param {string} assetUrl - asset url
 * @return {string|null} - transformed url
 */
function getAssetUrl(assetUrl) {
    let regex = getAdformAssetsDomainRegex();
    if (!assetUrl || !regex || !ADFORM_ASSETS_DOMAIN_REPLACEMENT) {
        return null;
    }

    try {
        return assetUrl.replace(new RegExp(regex, ''), ADFORM_ASSETS_DOMAIN_REPLACEMENT);
    } catch (e) {
        return null;
    }
}

/**
 * Parses response and returns array of bids (sorted by price descending).
 *
 * @param {Object} data - data from adform response
 * @param {int} numOfBids - how many bids to return
 * @return {[]} - top bids
 */
function parseTopBids(data, numOfBids) {
    try {
        if (!data || !numOfBids) {
            return [];
        }

        return data.seatbid[0].bid
            .filter((a) => a.price > 0)
            .sort((a, b) => b.price - a.price)
            .slice(0, numOfBids);
    } catch (e) {
        return [];
    }
}

/**
 * Builds bid object based on data from adform response.
 * Object has: (img, url, price, ...)
 *
 * @param {Object} bid - bid object from adform response
 * @return {Object|null} - parsed bid object or null if invalid/unexpected data
 */
function parseBidData(bid) {
    try {
        const native = bid.native;
        const descriptionAsset = JSON.parse(
            native.assets.find((asset) => asset.id === ASSET_ID_DESCRIPTION)?.data?.value ?? '{}'
        )[0];

        const desktopImg = native.assets.find((asset) => asset.id === ASSET_ID_DESKTOP_IMG)?.img;
        const desktopImgURL = desktopImg ? getAssetUrl(desktopImg.url) : null;

        const mobileImg = native.assets.find((asset) => asset.id === ASSET_ID_MOBILE_IMG)?.img;
        const mobileImgURL = mobileImg ? getAssetUrl(mobileImg.url) : null;

        const linkURL = native.link.url
            .replace('track.adform.net', adformTrackReplaceURL);

        let vanityURL = descriptionAsset ? descriptionAsset.assets?.vanity_url : null;
        if (vanityURL && vanityURL.indexOf(window.location.hostname) === -1) {
            vanityURL = new URL(vanityURL);
            vanityURL.hostname = window.location.hostname;
        }

        const impTrackerURL = native.imptrackers.find((url) => {
            return url.indexOf('/adfserve/') > -1;
        })
            .replace('track.adform.net', adformTrackReplaceURL);

        const price = bid.price;

        if (!price
            || !linkURL
            || !impTrackerURL
        ) {
            return null;
        }

        return {
            price: price,
            desktopImgURL: desktopImgURL || '',
            desktopImgWidth: desktopImg?.w || '',
            desktopImgHeight: desktopImg?.h || '',
            mobileImgURL: mobileImgURL || '',
            mobileImgWidth: mobileImg?.w || '',
            mobileImgHeight: mobileImg?.h || '',
            linkURL: linkURL,
            vanityURL: vanityURL,
            impTrackerURL: impTrackerURL,
            descriptionAsset: descriptionAsset,
            descriptionAltText: descriptionAsset?.altText || ''
        };
    } catch (e) {
        return null;
    }
}

/**
 * Builds product bid object based on data from adform response.
 * Object has: search data (brand id), tracking urls url, price, ...
 *
 * @param {Object} bid - bid object from adform response
 * @return {Object|null} - parsed bid object or null if invalid/unexpected data
 */
function parseProductBidData(bid) {
    try {
        const native = bid.native;
        const descriptionAsset = JSON.parse(
            native.assets.find((asset) => asset.id === ASSET_ID_DESCRIPTION).data.value
        )[0];

        const linkURL = native.link.url
            .replace('track.adform.net', adformTrackReplaceURL);

        const impTrackerURL = native.imptrackers.find((url) => {
            return url.indexOf('/adfserve/') > -1;
        })
            .replace('track.adform.net', adformTrackReplaceURL);

        const price = bid.price;

        if (!price
            || !descriptionAsset
            || !linkURL
            || !impTrackerURL
        ) {
            return null;
        }

        return {
            price: price,
            linkURL: linkURL,
            impTrackerURL: impTrackerURL,
            descriptionAsset: descriptionAsset
        };
    } catch (e) {
        return null;
    }
}

module.exports = {
    parseTopBids: parseTopBids,
    parseBidData: parseBidData,
    parseProductBidData: parseProductBidData
};
